import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchBettingHistory, validatePhoneNumber } from '../api/api';
import LoadingSpinner from '../components/LoadingSpinner';
import './HistoryPage.css';

const HistoryPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [phoneNumber, setPhoneNumber] = useState(location.state?.phoneNumber || '');
  const [inputPhone, setInputPhone] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedBet, setExpandedBet] = useState(null);

  useEffect(() => {
    if (location.state?.phoneNumber) {
      loadHistory(location.state.phoneNumber);
    }
  }, [location.state?.phoneNumber]);

  const loadHistory = async (phone) => {
    setLoading(true);
    setError('');
    
    try {
      const data = await fetchBettingHistory(phone);
      setHistory(data);
      setPhoneNumber(phone);
    } catch (err) {
      console.error('Error loading history:', err);
      setError('Failed to load betting history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setInputPhone(value);
      setError('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formattedPhone = inputPhone.startsWith('0') ? inputPhone : `0${inputPhone}`;
    
    if (!validatePhoneNumber(formattedPhone)) {
      setError('Please enter a valid phone number');
      return;
    }
    
    loadHistory(formattedPhone);
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-KE', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleExpand = (betId) => {
    setExpandedBet(expandedBet === betId ? null : betId);
  };

  return (
    <div className="history-page">
      <div className="page-header">
        <motion.div 
          className="header-content"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="page-title">
            <span className="title-icon">📜</span>
            Betting History
          </h1>
          {phoneNumber && (
            <p className="phone-display">
              Showing results for <strong>+254{phoneNumber.slice(1)}</strong>
            </p>
          )}
        </motion.div>
      </div>

      {/* Search Form */}
      {!phoneNumber && (
        <motion.div 
          className="search-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="search-card">
            <h2 className="search-title">Enter Your Phone Number</h2>
            <p className="search-description">
              View all your past bets and see what was in every box
            </p>
            
            <form className="search-form" onSubmit={handleSubmit}>
              <div className="search-input-wrapper">
                <span className="input-prefix">+254</span>
                <input
                  type="tel"
                  placeholder="712345678"
                  value={inputPhone}
                  onChange={handlePhoneChange}
                  className={error ? 'error' : ''}
                />
              </div>
              <motion.button
                type="submit"
                className="search-btn"
                disabled={inputPhone.length < 9}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                View History
              </motion.button>
            </form>
            {error && <span className="search-error">{error}</span>}
          </div>
        </motion.div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <LoadingSpinner text="Loading your betting history..." />
        </div>
      )}

      {/* History List */}
      {!loading && phoneNumber && (
        <div className="history-content">
          {history.length === 0 ? (
            <motion.div 
              className="empty-state"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <span className="empty-icon">📭</span>
              <h3>No Bets Found</h3>
              <p>You haven't placed any bets with this phone number yet.</p>
              <motion.button
                className="play-now-btn"
                onClick={() => navigate('/')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Start Playing Now
              </motion.button>
            </motion.div>
          ) : (
            <>
              <div className="history-stats">
                <div className="stat-card">
                  <span className="stat-icon">🎮</span>
                  <span className="stat-value">{history.length}</span>
                  <span className="stat-label">Total Bets</span>
                </div>
                <div className="stat-card win">
                  <span className="stat-icon">🏆</span>
                  <span className="stat-value">{history.filter(b => b.isWinner).length}</span>
                  <span className="stat-label">Wins</span>
                </div>
                <div className="stat-card">
                  <span className="stat-icon">💰</span>
                  <span className="stat-value">KES {history.length * 20}</span>
                  <span className="stat-label">Total Spent</span>
                </div>
              </div>

              <div className="history-list">
                {history.map((bet, index) => (
                  <motion.div
                    key={bet.id}
                    className={`history-item ${bet.isWinner ? 'winner' : 'loser'}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div 
                      className="bet-summary"
                      onClick={() => toggleExpand(bet.id)}
                    >
                      <div className="bet-icon">{bet.itemImage}</div>
                      
                      <div className="bet-info">
                        <div className="bet-header">
                          <h4 className="bet-title">{bet.itemName}</h4>
                          <span className={`bet-result-badge ${bet.isWinner ? 'win' : 'loss'}`}>
                            {bet.isWinner ? '🏆 WON' : '❌ LOST'}
                          </span>
                        </div>
                        <p className="bet-date">{formatDate(bet.timestamp)}</p>
                      </div>

                      <div className="bet-details">
                        <div className="bet-box">
                          <span className="label">Your Box</span>
                          <span className="value">Box {bet.boxChosen}</span>
                        </div>
                        <div className="bet-prize">
                          <span className="label">Result</span>
                          <span className={`value ${bet.isWinner ? 'gold' : ''}`}>
                            {bet.result.prize}
                          </span>
                        </div>
                      </div>

                      <motion.span 
                        className="expand-icon"
                        animate={{ rotate: expandedBet === bet.id ? 180 : 0 }}
                      >
                        ▼
                      </motion.span>
                    </div>

                    <AnimatePresence>
                      {expandedBet === bet.id && (
                        <motion.div
                          className="bet-expanded"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="all-boxes-section">
                            <h5 className="section-title">All Boxes Contents</h5>
                            <div className="all-boxes-grid">
                              {bet.allBoxContents.map((box, idx) => (
                                <div 
                                  key={idx}
                                  className={`box-item ${idx + 1 === bet.boxChosen ? 'selected' : ''} ${idx + 1 === bet.winningBox ? 'jackpot' : ''}`}
                                >
                                  <span className="box-num">Box {idx + 1}</span>
                                  <span className={`box-prize ${box.tier}`}>{box.prize}</span>
                                  {idx + 1 === bet.boxChosen && (
                                    <span className="box-tag yours">Your Pick</span>
                                  )}
                                  {idx + 1 === bet.winningBox && (
                                    <span className="box-tag jackpot">Jackpot</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>

              <div className="history-actions">
                <motion.button
                  className="change-number-btn"
                  onClick={() => {
                    setPhoneNumber('');
                    setHistory([]);
                    setInputPhone('');
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Search Another Number
                </motion.button>
                <motion.button
                  className="play-again-btn"
                  onClick={() => navigate('/')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Play Again
                </motion.button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoryPage;

